import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {SchemaInterfacesWithFieldsQuery} from 'lookup-config/first-lookup-step/__generated/SchemaInterfacesWithFieldsQuery';
import {
  ContainerByGridPositionLookupStepSpec,
  FormErrors,
  LookupConfigModalContextInfo,
  LookupStepInfo,
  LookupStepType,
} from 'lookup-config/LookupConfigModalConstants';
import {getLookupFormErrorMessage} from 'lookup-config/utils/getFormErrors';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import APIUtil from 'util/APIUtil';

import {canFieldBeBox, canFieldBePlate, getSchemaFieldsForLookupStep} from '../LookupConfigUtils';
import LookupStepForm, {isLookupStepConfigurable} from '../LookupStepForm';
import {
  getLookupStepInfoForCountStepItem,
  getLookupStepInfoForFlowchartProcessTaskStepItem,
  getLookupStepInfoForFieldStepItem,
  getLookupStepInfoForNonFieldStepItem,
  getLookupStepInfoForSourceDestinationStepItem,
  getLookupStepInfoForContainerByGridPositionStepItem,
} from '../LookupStepInitializerUtils';
import {LookupStepSectionHeader} from '../LookupStepSectionHeader';

function getFirstLookupStepsForNonNotebook(
  firstLookupStepTypes: Array<LookupStepType>,
  baseSchema: Schema | null,
  context: LookupConfigModalContextInfo,
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean
): Array<LookupStepInfo> {
  const lookupStepInfos: Array<LookupStepInfo> = [];
  const {sourceLookupInfos, destinationLookupInfos, processTaskSchema, flowchartTaskSchema} = context;
  const usePlateLayout = context.usePlateLayout ?? false;
  const isValidBaseSchemaField = (field: SchemaField): boolean =>
    processTaskSchema == null || !field.isSnapshot;

  const baseSchemaFields = baseSchema
    ? getSchemaFieldsForLookupStep(baseSchema, true, schemaFieldFilterFn).filter((field) =>
        isValidBaseSchemaField(field)
      )
    : [];
  for (const stepType of firstLookupStepTypes) {
    switch (stepType) {
      case LookupStepType.SOURCE:
        if (sourceLookupInfos) {
          lookupStepInfos.push(
            getLookupStepInfoForSourceDestinationStepItem(
              LookupStepType.SOURCE,
              sourceLookupInfos,
              baseSchema,
              usePlateLayout
            )
          );
        }
        break;
      case LookupStepType.DESTINATION:
        if (destinationLookupInfos) {
          lookupStepInfos.push(
            getLookupStepInfoForSourceDestinationStepItem(
              LookupStepType.DESTINATION,
              destinationLookupInfos,
              baseSchema,
              usePlateLayout
            )
          );
        }
        break;
      case LookupStepType.SCHEMA_FIELD:
        for (const field of baseSchemaFields) {
          lookupStepInfos.push(
            getLookupStepInfoForFieldStepItem(LookupStepType.SCHEMA_FIELD, field, baseSchema)
          );
        }
        break;
      case LookupStepType.COUNT:
        if (baseSchemaFields.length) {
          lookupStepInfos.push(getLookupStepInfoForCountStepItem(baseSchema, schemaFieldFilterFn, true));
        }
        break;
      case LookupStepType.CONSTANT:
      case LookupStepType.PLACEHOLDER_BOXES:
      case LookupStepType.PLACEHOLDER_PLATES:
        lookupStepInfos.push(getLookupStepInfoForNonFieldStepItem(stepType, baseSchema));
        break;
      case LookupStepType.PROCESS_TASK:
        if (APIUtil.isProcessOutputSchema(baseSchema!.id)) {
          lookupStepInfos.push(getLookupStepInfoForNonFieldStepItem(stepType, baseSchema));
        }
        break;
      case LookupStepType.FLOWCHART_PROCESS_TASK:
        if (APIUtil.isProcessTaskSchema(baseSchema!.id) || APIUtil.isProcessOutputSchema(baseSchema!.id)) {
          // We cannot look up the root task of the root of the current flowchart.
          if (flowchartTaskSchema != null && baseSchema!.id !== flowchartTaskSchema.id) {
            lookupStepInfos.push(
              getLookupStepInfoForFlowchartProcessTaskStepItem(flowchartTaskSchema, baseSchema)
            );
          }
        }
        break;
      case LookupStepType.CONTAINER_BY_GRID_POSITION:
        for (const field of baseSchemaFields) {
          if (canFieldBeBox(field) || canFieldBePlate(field)) {
            const lookupStep: ContainerByGridPositionLookupStepSpec = {
              type: LookupStepType.CONTAINER_BY_GRID_POSITION,
              schemaField: field ? field.apiId : '',
              gridPositionSchemaField: null,
            };

            lookupStepInfos.push(getLookupStepInfoForContainerByGridPositionStepItem(lookupStep, baseSchema));
          }
        }
        break;
      default:
        assert(false, `Unexpected first step type ${stepType}`);
        break;
    }
  }
  return lookupStepInfos;
}

export interface DefaultFirstLookupStepFormProps {
  disabled: boolean;
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
  firstLookupStepTypes: Array<LookupStepType>;
  context?: LookupConfigModalContextInfo;
  schemaFieldFilterFn?: (field: SchemaField, excludeNonLinkFields: boolean) => boolean;
  allowAlternatingGridPatterns?: boolean;
  baseSchema: Schema | null;
  lookupStepGroup: Array<LookupStepInfo>;
  // Caution: this callback is shared by SchemaFieldSelectTree. The SchemaFieldSelectTree itself is well-behaved,
  // and it will not blow anything away, but this component should be careful not to blow away lookupStepGroup[1]
  // unless the schema has actually changed and the SchemaFieldSelectTree actually needs to reset.
  handleSetLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  handleAddLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
  formErrors: FormErrors;
  basePaneInvalidLookupObjectErrorMessage?: () => JSX.Element;
}

export const SCHEMA_INTERFACES_WITH_FIELDS_QUERY = gql`
  query SchemaInterfacesWithFieldsQuery @skipAudit @engComponent(name: SCHEMAS) {
    allSchemaInterfaces {
      ...SchemaInterfaceWithFieldsGraphQL
    }
  }
  ${getFragmentWrapper('SchemaInterfaceWithFields').fragment}
`;

export function DefaultFirstLookupStepForm(props: DefaultFirstLookupStepFormProps): JSX.Element {
  const options = getFirstLookupStepsForNonNotebook(
    props.firstLookupStepTypes,
    props.baseSchema,
    props.context ?? {},
    props.schemaFieldFilterFn
  );
  const firstLookupStep = props.lookupStepGroup[0];

  return (
    <>
      <LookupStepSectionHeader
        isCollapsed={props.isCollapsed}
        setIsCollapsed={props.setIsCollapsed}
        isActive={!props.disabled}
        lookupStepGroup={props.lookupStepGroup}
        parentSchema={props.baseSchema}
        error={props.formErrors.missingField ? 'Missing field' : null}
      />
      {!props.isCollapsed && (
        <>
          <Row className={css(styles.FirstLookupStepFormSection)}>
            <Col md={6}>
              <FieldLabel label="Start from" labelStyle={LabelStyle.LABEL} />
              <SelectDropdown
                disabled={props.disabled}
                alwaysDropDown={true}
                menu={
                  <SearchableMenu
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                    selectedValue={firstLookupStep}
                    onSelect={(option: LookupStepInfo) => {
                      props.handleSetLookupStepGroup([option]);
                    }}
                    optionDatas={options.map((option) => ({name: option.name, value: option}))}
                  />
                }
                displayName={firstLookupStep ? firstLookupStep.name : 'Select lookup'}
                error={getLookupFormErrorMessage(
                  'Missing field',
                  props.formErrors,
                  'missingField',
                  props.basePaneInvalidLookupObjectErrorMessage
                )}
                dataTestAttrs={{
                  component: 'DefaultFirstLookupStepForm',
                  element: 'startFromLookupDropdown',
                }}
              />
            </Col>
            <Col md={6}>
              {firstLookupStep && isLookupStepConfigurable(firstLookupStep.lookupStep) ? (
                <LookupStepForm
                  disabled={props.disabled}
                  lookupStepInfo={firstLookupStep}
                  handleSetLookupStep={(lookupStepInfo: LookupStepInfo) => {
                    props.handleSetLookupStepGroup([lookupStepInfo]);
                  }}
                  context={props.context}
                  allowAlternatingGridPatterns={props.allowAlternatingGridPatterns}
                  schemaFieldFilterFn={props.schemaFieldFilterFn}
                  formErrors={props.formErrors}
                  isFirstStep={true}
                />
              ) : null}
            </Col>
          </Row>
        </>
      )}
    </>
  );
}

export default function QueryingDefaultFirstLookupStepForm(
  props: DefaultFirstLookupStepFormProps
): JSX.Element {
  return (
    <BenchlingQuery<SchemaInterfacesWithFieldsQuery>
      query={SCHEMA_INTERFACES_WITH_FIELDS_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {(data) => <DefaultFirstLookupStepForm {...props} />}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  FirstLookupStepFormSection: {
    marginTop: '20px',
    marginBottom: '20px',
  },
});
